
import { EventSourcePolyfill as EventSource } from 'event-source-polyfill';
import { supabase } from '@/integrations/supabase/client';
import { Json } from '@/integrations/supabase/types';
import { getUserInfo, UserInfo } from './userInfo';

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  components?: Component[];
  timestamp?: string;
}

export interface Component {
  type: string;
  props: Record<string, any>;
}

export interface DealerListing {
  dealerName: string;
  mileage: number;
  price: number;
  link: string;
  reviews?: DealerReviews;
}

export interface DealerReviews {
  rating: number;
  totalReviews: number;
  review_content?: string[];  // Added this field
}

export interface CompetitiveListing {
  dealerName: string;
  mileage: number;
  price: number;
  link: string;
  imageUrl?: string;
  reviews?: {
    rating: number;
    totalReviews: number;
    review_content?: string[];  // Added this field
  };
}

export interface Product {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  price: string;
  link: string;
  specifications?: {
    year?: string;
    make?: string;
    model?: string;
    trim?: string;
    mileage?: number;
  };
  competitiveListings?: CompetitiveListing[];
}

export class Session {
  private sessionId: string | null = null;
  private eventSource: EventSource | null = null;
  private userInfo: UserInfo | null = null;

  constructor(sessionId?: string) {
    this.sessionId = sessionId || null;
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  async start(): Promise<string | null> {
    try {
      if (!this.sessionId) {
        // New session
        this.sessionId = crypto.randomUUID();
        this.userInfo = await getUserInfo();
        const pickupLine = await this.getPickupLine();
        await this.persistMessage({
          role: 'assistant',
          content: pickupLine,
          timestamp: new Date().toISOString()
        });
        return pickupLine;
      } else {
        // Existing session
        this.userInfo = await getUserInfo();
        return null;
      }
    } catch (error) {
      console.error('Error starting session:', error);
      throw error;
    }
  }

  private async getPickupLine(): Promise<string> {
    try {
      const { data, error } = await supabase.functions.invoke('chat', {
        body: {
          message: `Generate nothing but a message like this:

Hi! 🛡️ Ever feel like car dealers hold all the power? Meet your AI Negotiator—I\'m here to arm you with insider secrets and fair deals. Let\'s start with a tip to save you $1,000s: [e.g., \'SUVs with adaptive cruise control slash insurance costs by 20%!\']. Ready to conquer the lot on your terms? 🚗💥`,
          sessionId: this.sessionId,
          userInfo: this.userInfo
        }
      });

      if (error) throw error;
      if (!data) throw new Error('No response from chat function');

      return data.content;
    } catch (error) {
      console.error('Error in getPickupLine:', error);
      throw error;
    }
  }

  async sendMessage(message: string, onUpdate: (message: Message) => void): Promise<void> {
    if (!this.sessionId) throw new Error('Session not started');

    try {
      // Persist user message first
      await this.persistMessage({
        role: 'user',
        content: message,
        timestamp: new Date().toISOString()
      });

      const { data, error } = await supabase.functions.invoke('chat', {
        body: {
          message,
          sessionId: this.sessionId,
          userInfo: this.userInfo
        }
      });

      if (error) throw error;
      if (!data) throw new Error('No response from chat function');

      // Add timestamp to assistant's response
      const responseWithTimestamp = {
        ...data,
        timestamp: new Date().toISOString()
      };

      // Persist assistant's response
      await this.persistMessage(responseWithTimestamp);
      onUpdate(responseWithTimestamp);
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  private async persistMessage(message: Message): Promise<void> {
    if (!this.sessionId) throw new Error('Session not started');

    try {
      const { error } = await supabase.from('messages').insert({
        session_id: this.sessionId,
        role: message.role,
        content: message.content,
        components: message.components as unknown as Json,
        created_at: message.timestamp
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error persisting message:', error);
      throw error;
    }
  }

  async loadMessages(): Promise<Message[]> {
    if (!this.sessionId) throw new Error('Session not started');

    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('session_id', this.sessionId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return data.map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        components: msg.components ? (msg.components as unknown as Component[]) : undefined,
        timestamp: msg.created_at
      }));
    } catch (error) {
      console.error('Error loading messages:', error);
      throw error;
    }
  }

  cleanup(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}
